import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { errorNotify } from 'app/store/common-effects/notifier.effects';
import {
  CourierOutput,
  OrderedGoodInput,
  OrderInput,
  OrderInputAdditions,
  RecipientOutput,
  SenderOutput,
  StandingDataInput,
  UnloadedOnInput,
} from 'core/http/project';
import * as XLSX from 'xlsx';

interface CreateOrdersData {
  couriers: CourierOutput[];
  senders: SenderOutput[];
}

interface CollectSuccess { type: 'success'; orders: OrderInput[] }
interface CollectError { type: 'error'; message: string }
export type CollectResult = CollectError | CollectSuccess;

@Component({
  selector: 'app-create-orders-dialog',
  templateUrl: './create-orders-dialog.component.html',
  styleUrls: ['./create-orders-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateOrdersDialogComponent {
  private _couriers = new Array<CourierOutput>();
  private _senders = new Array<SenderOutput>();
  private _form = new UntypedFormGroup({
    courier: new UntypedFormControl('', Validators.required),
    sender: new UntypedFormControl('', Validators.required),
  });

  private _ordersInput = new Array<OrderInput>();

  get senders(): SenderOutput[] {
    return this._senders;
  }

  get couriers(): CourierOutput[] {
    return this._couriers;
  }

  get form(): UntypedFormGroup {
    return this._form;
  }

  get ordersInput(): OrderInput[] {
    return this._ordersInput;
  }

  set ordersInput(value: OrderInput[]) {
    this._ordersInput = value;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) data: CreateOrdersData,
    private store$: Store,
    private translateService: TranslateService,
  ) {
    this._couriers = data.couriers;
    this._senders = data.senders;
  }

  clearFile(): void {
    this.ordersInput.length = 0;
  }

  addNewFile(targetFile: File, input: HTMLInputElement): void {
    const reader: FileReader = new FileReader();
    reader.readAsBinaryString(targetFile);

    reader.onload = (file: any) => {
      const basicString: string = file.target.result;
      const workBook: XLSX.WorkBook = XLSX.read(basicString, { type: 'binary' });

      const workSheetName: string = workBook.SheetNames[0];
      const pageData = workBook.Sheets[workSheetName];

      const data: any[] = XLSX.utils.sheet_to_json(pageData, { header: 'A' });

      const result = this.collectOrderInput(data);

      switch (result.type) {
        case 'error':
          this.store$.dispatch(errorNotify({ message: result.message, translate: false }));

          // Без этого нельзя выбрать один файл 2 раза подряд
          input.value = '';
          break;
        case 'success':
          this.ordersInput = result.orders;
          break;
      }

      if (this.ordersInput.length > 100) {
        this.store$.dispatch(errorNotify({ message: 'ERROR.orders_limit' }));
      }
    };
  }

  collectOrderInput(data: any[]): CollectResult {
    try {
      const orders = data.map((row, i) => {
        const recipient: RecipientOutput = {
          country: row.F,
          region: row.G,
          city: row.H,
          address: row.I,
          house: row.J.toString(),
          flat: row.K.toString(),
          postcode: row.L.toString(),
          name: row.M,
          phone: row.N,
          email: row.O,
        };

        const unloadedOn: UnloadedOnInput = {
          courierId: this.form.value.courier,
          senderId: this.form.value.sender,
          track: row.P,
        };

        const lgStandingData: StandingDataInput = {
          outProjectId: +row.R || null,
          outOrderId: +row.S || null,
          outProjectName: row.T || null,
          operatorLogin: row.U || null,
          operatorId: +row.V || null,
        };

        return {
          outsideId: row.A,
          orderedGoods: this.convertToOrderedGoods(row.B, i + 1),
          shippingCost: row.C,
          totalPrice: row.D,
          comment: row.E,
          recipient,
          additions: this.convertToAdditionFields(row.Q, i + 1),
          unloadedOn,
          lgStandingData,
        } as OrderInput;
      });
      return {
        type: 'success',
        orders,
      };
    }
    catch (err) {
      return {
        type: 'error',
        message: err instanceof Error ? err.message : String(err),
      };
    }
  }

  convertToOrderedGoods(str: string, index: number): OrderedGoodInput[] {
    const cart: string[] = str.split(';');

    return cart.map((item) => {
      const info = item.trim().split(' ');

      if (info.length > 3) {
        throw new Error(this.translateService.instant('ORDERS.IMPORT.invalid_column', { column: 'B', row: index }));
      }

      return {
        alias: info[0],
        quantity: +info[1],
        price: +info[2],
      } as OrderedGoodInput;
    });
  }

  convertToAdditionFields(str: string, index: number): OrderInputAdditions {
    const arr: string[] = str.split(';');

    return arr.reduce((prev, curr) => {
      const info = curr.trim().split(' ');

      if (info.length > 2) {
        throw new Error(this.translateService.instant('ORDERS.IMPORT.invalid_column', { column: 'Q', row: index }));
      }

      const key = `addition${info[0]}`;
      return {
        ...prev,
        [key]: info[1],
      };
    }, {}) as OrderInputAdditions;
  }
}
